import { useState } from 'react';

import { DYNAMIC_GITLAB_INSTANCES } from '../../../../../shared/featureFlags/flagNames';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { window } from '../../../../globals';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { getAuthorizeServiceURL } from '../../../../helpers/services';
import useGetGitLabSettings from '../../../../hooks/api/settings/integrations/useGetGitLabSettings';
import { post } from '../../../../redux/api';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import { BUTTON_TYPE, NavigationButton } from '../../../generic';
import Button from '../../../generic/Button';
import Icon, { icons } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';

import GitLabInstanceModal from './GitLabInstanceModal';
import GitLabInstanceRow from './GitLabInstanceRow';

const GitlabSettings = () => {
  const { onConfirm } = useDialogContext();

  const hasPermission = useHasPermission();
  const hasEditIntegrationSettingsPermission = hasPermission({
    resourceType: ResourceType.OrganizationIntegrationSettings,
    action: Action.Edit,
  });

  const isDynamicGitLabInstancesEnabled = useIsFeatureEnabled(DYNAMIC_GITLAB_INSTANCES);
  const { data, isLoading } = useGetGitLabSettings(
    window.user?.organizationId ?? 0,
    isDynamicGitLabInstancesEnabled && hasEditIntegrationSettingsPermission,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Header heading="GitLab" />

      <Section>
        <Section.Left heading="GitLab Login">
          <Section.Paragraph>
            FOSSA allows users to link a GitLab login to their FOSSA login.
          </Section.Paragraph>
        </Section.Left>
        <Section.Right>
          {!window.user?.gitlab ? (
            <>
              <h4>You are not connected to GitLab</h4>
              <div className="margin-top-lg">
                <NavigationButton
                  to={getAuthorizeServiceURL({
                    vcsHost: 'gitlab',
                    location: window.location.href,
                    serviceConnect: true,
                  })}
                  type={BUTTON_TYPE.SECONDARY}
                  openInNewTab
                >
                  <Icon icon={icons.gitlab} /> Connect to GitLab
                </NavigationButton>
              </div>
            </>
          ) : (
            <>
              <h4>
                You are currently connected{' '}
                {window.user?.gitlab?.name ? (
                  <span>
                    as <b>{window.user?.gitlab?.name}</b>
                  </span>
                ) : (
                  <span>to GitLab</span>
                )}
              </h4>
              <div className="margin-top-lg">
                <Button
                  type={BUTTON_TYPE.SECONDARY}
                  onClick={() => {
                    onConfirm({
                      title: 'Confirm GitLab disconnect',
                      body: (
                        <div>
                          <p>
                            <b>Are you sure you want to disassociate the current user with the registered GitLab account?</b>{' '}
                            (cannot be undone)
                          </p>
                        </div>
                      ),
                      onConfirm: async () => {
                        await post('/api/services/gitlab/disconnectService', {});
                        window.location.reload();
                      },
                    });
                  }}
                >
                  Disconnect
                </Button>
              </div>
            </>
          )}
        </Section.Right>
      </Section>

      {isDynamicGitLabInstancesEnabled && hasEditIntegrationSettingsPermission && (
        <>
          <Section>
            <Section.Left heading="GitLab Instances">
              <Section.Paragraph>
                Configure self-managed GitLab instances for your organization.
                Each instance requires OAuth2 application credentials.
              </Section.Paragraph>
            </Section.Left>
            <Section.Right>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                  {(data?.instances ?? []).map((instance) => (
                    <GitLabInstanceRow
                      key={instance.id}
                      instance={instance}
                    />
                  ))}
                </>
              )}
              <Button
                type={BUTTON_TYPE.SECONDARY}
                onClick={() => setIsModalOpen(true)}
              >
                <Icon icon={icons.add} /> Add Instance
              </Button>
            </Section.Right>
          </Section>
          <GitLabInstanceModal
            isShowing={isModalOpen}
            onHide={handleModalClose}
          />
        </>
      )}
    </>
  );
};

export default GitlabSettings;
